/**
 * Which side of the deal lifecycle a gap belongs to. Persisted on the gap;
 * documents written before the field existed are read as {@link GapCategory.DELIVERY}.
 */
export var GapCategory;
(function (GapCategory) {
    GapCategory["SALES"] = "sales";
    GapCategory["DELIVERY"] = "delivery";
})(GapCategory || (GapCategory = {}));
/**
 * How much the customer has to do for a gap's fix to land.
 *
 * An ordered ladder: {@link GapCustomerFrictionLevel.NONE} is no friction with
 * the client at all — nothing reaches them — and every level above it asks more
 * of them. The enrichment prompt defines each rung in full; this is the
 * vocabulary a published gap ends on.
 *
 * Deliberately not the run contract's `GapRunLevel` / `GapRunRevisedLevel`. The
 * finder scores on three levels with no `NONE`, so it files a purely internal
 * action as `LOW`; the validator has a `NONE` but spends it on a CONTRADICTED
 * gap. Here `NONE` is a real reading of a real gap.
 */
export var GapCustomerFrictionLevel;
(function (GapCustomerFrictionLevel) {
    /** Nothing reaches them — the fix is ours end to end. */
    GapCustomerFrictionLevel["NONE"] = "NONE";
    /** Something reaches them; they act on none of it. */
    GapCustomerFrictionLevel["LOW"] = "LOW";
    /** They act: their own time, attention or decision. */
    GapCustomerFrictionLevel["MEDIUM"] = "MEDIUM";
    /**
     * It costs them beyond their own effort — money, a commitment reopened, or a
     * process of their own they must change and defend to others.
     */
    GapCustomerFrictionLevel["HIGH"] = "HIGH";
})(GapCustomerFrictionLevel || (GapCustomerFrictionLevel = {}));
/** A human label on a gap: was this actually a relevant gap, or noise? */
export var GapFeedbackVote;
(function (GapFeedbackVote) {
    GapFeedbackVote["RELEVANT"] = "relevant";
    GapFeedbackVote["NOT_RELEVANT"] = "not_relevant";
})(GapFeedbackVote || (GapFeedbackVote = {}));
/**
 * How a timeline event relates to the gap's escalation — one kind per event.
 * Belongs to the refined {@link GapEscalationEvent} persisted on the gap; the
 * run contract no longer carries event kinds, since a run gap records only its
 * single drift point (see `GapRunDrift` in `gap-run.domain.ts`) rather than a
 * series of events.
 *
 * Values are lowercase, unlike the UPPERCASE run-contract enums, because that is
 * the vocabulary the escalation refiner writes.
 */
export var GapTimelineEventKind;
(function (GapTimelineEventKind) {
    /** Neutral lifecycle anchor (contract signed, kickoff, opportunity created). */
    GapTimelineEventKind["MILESTONE"] = "milestone";
    /** An earlier signal that fed the picture but was not enough on its own. */
    GapTimelineEventKind["EARLY_WARNING"] = "early_warning";
    /** The final fact that made the gap knowable — about the evidence. */
    GapTimelineEventKind["DRIFT"] = "drift";
    /** The moment the gap actually caused a problem — about the deal. */
    GapTimelineEventKind["INCIDENT"] = "incident";
    /** Intera catching the gap. Exactly one per timeline. */
    GapTimelineEventKind["INTERA_FLAG"] = "intera_flag";
    /** A concrete, documented future deadline the gap still threatens. */
    GapTimelineEventKind["UPCOMING"] = "upcoming";
})(GapTimelineEventKind || (GapTimelineEventKind = {}));
